import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { DeviceMobile, X, ShareNetwork, Plus } from "@phosphor-icons/react";

const DISMISSED_KEY = "sf_install_dismissed_at";
const DISMISS_DAYS = 14;

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isIOS() {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

function recentlyDismissed() {
  try {
    const t = Number(localStorage.getItem(DISMISSED_KEY) || 0);
    if (!t) return false;
    return Date.now() - t < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch (e) { return false; }
}

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [open, setOpen] = useState(false);
  const [ios, setIos] = useState(false);

  useEffect(() => {
    if (isStandalone() || recentlyDismissed()) return;

    if (isIOS()) {
      setIos(true);
      // Slight delay so it doesn't crowd the first paint
      const t = setTimeout(() => setOpen(true), 2500);
      return () => clearTimeout(t);
    }

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setOpen(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstall);
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(DISMISSED_KEY, String(Date.now())); } catch (e) {}
    setOpen(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    setDeferred(null);
    setOpen(false);
    if (outcome !== "accepted") dismiss();
  };

  if (!open) return null;

  return (
    <div
      data-testid="install-prompt"
      className="fixed bottom-4 left-4 right-4 z-50 md:left-auto md:right-6 md:bottom-6 md:max-w-sm border-2 border-ink-900 bg-white shadow-[6px_6px_0_0_#FF4D00] animate-fade-up"
    >
      <div className="flex items-start gap-3 p-4">
        <div className="w-10 h-10 bg-ink-900 flex items-center justify-center shrink-0">
          <DeviceMobile size={20} weight="bold" className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-safety">Phone app</div>
          <div className="mt-1 font-display font-bold text-lg text-ink-900 leading-tight">Install SafeForm on your phone</div>
          <p className="mt-1 text-sm text-ink-700 leading-relaxed">
            {ios
              ? "Record from anywhere on the floor. Tap the share icon, then 'Add to Home Screen'."
              : "Capture and analyze repetitive motion from the job site — works offline-tolerant, opens straight to your case log."}
          </p>

          {ios ? (
            <div className="mt-3 flex items-center gap-2 text-xs text-ink-700">
              <ShareNetwork size={14} weight="bold" />
              <span>Share</span>
              <span className="text-ink-300">→</span>
              <Plus size={14} weight="bold" />
              <span>Add to Home Screen</span>
            </div>
          ) : (
            <div className="mt-3 flex items-center gap-2">
              <Button
                data-testid="install-prompt-install"
                onClick={install}
                size="sm"
                className="rounded-none bg-safety hover:bg-safety-hover text-white"
              >
                Install
              </Button>
              <Button
                data-testid="install-prompt-dismiss"
                onClick={dismiss}
                variant="outline"
                size="sm"
                className="rounded-none border-ink-100 hover:border-ink-900"
              >
                Not now
              </Button>
            </div>
          )}
        </div>
        <button
          onClick={dismiss}
          aria-label="Dismiss"
          data-testid="install-prompt-close"
          className="text-ink-500 hover:text-ink-900"
        >
          <X size={16} weight="bold" />
        </button>
      </div>
    </div>
  );
}
